import {
  Alert,
  Anchor,
  Box,
  Group,
  Loader,
  Select,
  Stack,
  Table,
  Text,
} from '@mantine/core';
import dayjs from 'dayjs';
import { useState } from 'react';
import { PiInfo } from 'react-icons/pi';
import {
  type TDocumentKind,
  type TVersionChange,
  type TVersionEntry,
  useDocumentView,
} from '@/models/invoices';

function DocumentFrame({ html }: { html: string }) {
  const [height, setHeight] = useState(1100);
  return (
    <iframe
      title="Document"
      srcDoc={html}
      sandbox="allow-same-origin"
      style={{ width: '100%', height, border: 0, background: 'white' }}
      onLoad={(e) => {
        const doc = e.currentTarget.contentDocument;
        if (doc) setHeight(doc.documentElement.scrollHeight + 16);
      }}
    />
  );
}

function ChangeRow({ c }: { c: TVersionChange }) {
  return (
    <Table.Tr>
      <Table.Td>
        <Text fz="xs" fw={600}>
          {c.label}
        </Text>
        {c.item && (
          <Text fz={11} c="dimmed" lineClamp={2}>
            {c.item}
          </Text>
        )}
      </Table.Td>
      <Table.Td c="dimmed" style={{ wordBreak: 'break-word' }}>
        {c.from}
      </Table.Td>
      <Table.Td style={{ wordBreak: 'break-word' }}>{c.to}</Table.Td>
    </Table.Tr>
  );
}

/**
 * What the selected version changed against the one before: what was changed and the total it
 * came to. What only followed from it (re-priced lines, rates, the account) is folded away.
 */
function ChangesTable({ changes }: { changes: TVersionChange[] }) {
  const [all, setAll] = useState(false);
  if (changes.length === 0) {
    return (
      <Text fz="xs" c="dimmed" mt={6}>
        The first version: nothing changed before it.
      </Text>
    );
  }
  const key = (c: TVersionChange, i: number) =>
    `${c.field}-${c.item ?? ''}-${i}`;
  const main = changes.filter((c) => !c.effect);
  const effects = changes.filter((c) => c.effect);
  return (
    <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
      <Table fz="xs" verticalSpacing={4} horizontalSpacing="xs" withTableBorder>
        <Table.Thead>
          <Table.Tr>
            <Table.Th w="30%">Changed</Table.Th>
            <Table.Th w="35%">Before</Table.Th>
            <Table.Th w="35%">After</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {main.map((c, i) => (
            <ChangeRow key={key(c, i)} c={c} />
          ))}
          {all && effects.map((c, i) => <ChangeRow key={key(c, i)} c={c} />)}
        </Table.Tbody>
      </Table>
      {effects.length > 0 && (
        <Anchor
          component="button"
          fz="xs"
          c="dimmed"
          style={{ alignSelf: 'flex-start' }}
          onClick={() => setAll(!all)}
        >
          {all
            ? 'Hide resulting changes'
            : `Show ${effects.length} resulting change${effects.length === 1 ? '' : 's'}`}
        </Anchor>
      )}
    </Stack>
  );
}
const when = (v: string) => dayjs(v).format('DD.MM.YYYY HH:mm');

/** Who made the selected version; whether it was sent is on the related documents and on Send. */
function VersionFacts({ v }: { v: TVersionEntry | undefined }) {
  if (!v) return null;
  return (
    <Stack gap={0}>
      <Text fz={11} c="dimmed">
        {v.createdByName ?? 'System'}
      </Text>
      {v.label && (
        <Text fz={11} c="orange">
          {v.label}
        </Text>
      )}
    </Stack>
  );
}
/**
 * The document area: the selected version as it was stored, rendered from its snapshot, with
 * the version picker, who made the version and whether it was sent, and what it changed.
 */
export function DocumentView({
  kind,
  id,
  version,
  versions,
  onSelectVersion,
}: {
  kind: TDocumentKind;
  id: number;
  /** null = the current version. */
  version: number | null;
  versions?: TVersionEntry[];
  onSelectVersion?: (version: number | null) => void;
}) {
  const q = useDocumentView(kind, id, version);
  const view = q.data;

  if (q.isLoading) {
    return (
      <Group justify="center" py="xl">
        <Loader size="sm" />
      </Group>
    );
  }
  if (!view) {
    return (
      <Alert color="red" icon={<PiInfo />}>
        The document could not be loaded.
      </Alert>
    );
  }

  return (
    <Stack gap="sm">
      {versions && versions.length > 1 && onSelectVersion && (
        <Group align="flex-start" gap="md" wrap="nowrap">
          <Stack gap={4} w={230} style={{ flexShrink: 0 }}>
            <Select
              size="xs"
              allowDeselect={false}
              comboboxProps={{ width: 'max-content', position: 'bottom-start' }}
              styles={{ option: { whiteSpace: 'nowrap' } }}
              value={String(view.version ?? '')}
              onChange={(v) => {
                const n = Number(v);
                onSelectVersion(n === view.latest ? null : n);
              }}
              data={versions.map((v) => ({
                value: String(v.version),
                label: `v${v.version}${v.version === view.latest ? ' (latest)' : ''} · ${when(v.createdAt)}`,
              }))}
            />
            <VersionFacts
              v={versions.find((v) => v.version === view.version)}
            />
          </Stack>
          <ChangesTable key={view.version} changes={view.changes} />
        </Group>
      )}
      {view.error && (
        <Alert color="red" icon={<PiInfo />}>
          This document cannot be rendered: {view.error}
        </Alert>
      )}
      {view.html && (
        <Box
          style={{
            border: '1px solid var(--mantine-color-default-border)',
            borderRadius: 6,
            overflow: 'hidden',
          }}
        >
          <DocumentFrame
            key={`${kind}-${id}-${view.version}`}
            html={view.html}
          />
        </Box>
      )}
    </Stack>
  );
}
